import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions, storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  FileText,
  Upload,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  History,
  Loader2,
  X,
  RefreshCw
} from 'lucide-react';

const FornecedorDocumentosPage = () => {
  const { user, userData, getEmpresaId } = useAuth();
  const [documentos, setDocumentos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [arquivo, setArquivo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadDocumentos();
  }, []);

  const loadDocumentos = async () => {
    try {
      setLoading(true);
      setError('');
      
      const fornecedorId = getEmpresaId(); // Para fornecedor, getEmpresaId() retorna id_fornecedor
      
      const getDocumentos = httpsCallable(functions, 'getDocumentosObrigatoriosFornecedor');
      const result = await getDocumentos({ id_fornecedor: fornecedorId });
      
      if (result.data.success) {
        setDocumentos(result.data.data.documentos || []);
        setStats(result.data.data.stats || {});
      }
    } catch (err) {
      console.error('❌ Erro ao carregar documentos:', err);
      setError('Erro ao carregar documentos obrigatórios.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUploadModal = (doc) => {
    setSelectedDoc(doc);
    setShowUploadModal(true);
    setArquivo(null);
    setError('');
    setSuccess('');
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedDoc(null);
    setArquivo(null);
    setError('');
    setSuccess('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar formato
    const extensao = file.name.split('.').pop()?.toUpperCase();
    if (!selectedDoc.formato_aceito.includes(extensao)) {
      setError(`Formato não aceito. Formatos permitidos: ${selectedDoc.formato_aceito.join(', ')}`);
      return;
    }

    // Validar tamanho
    const tamanhoMB = file.size / (1024 * 1024);
    if (tamanhoMB > selectedDoc.tamanho_maximo_mb) {
      setError(`Arquivo muito grande. Tamanho máximo: ${selectedDoc.tamanho_maximo_mb}MB`);
      return;
    }

    setArquivo(file);
    setError('');
  };

  const handleUpload = async () => {
    if (!arquivo) {
      setError('Selecione um arquivo para enviar.');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const fornecedorId = getEmpresaId();
      
      // Upload para Storage
      const storageRef = ref(storage, `documentos/${fornecedorId}/${selectedDoc.id}/${Date.now()}_${arquivo.name}`);
      const uploadResult = await uploadBytes(storageRef, arquivo);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      // Registrar no Firestore
      const uploadDoc = httpsCallable(functions, 'uploadDocumentoObrigatorio');
      const result = await uploadDoc({
        fornecedor_id: fornecedorId,
        documento_obrigatorio_id: selectedDoc.id,
        nome_arquivo: arquivo.name,
        url_arquivo: downloadURL,
        tamanho_arquivo: arquivo.size,
        tipo_arquivo: arquivo.type
      });

      if (result.data.success) {
        setSuccess('Documento enviado com sucesso! Aguarde análise da equipe de qualidade.');
        setTimeout(() => {
          handleCloseUploadModal();
          loadDocumentos();
        }, 2000);
      } else {
        setError(result.data.message || 'Erro ao enviar documento.');
      }
    } catch (err) {
      console.error('❌ Erro ao fazer upload:', err);
      setError('Erro ao enviar documento. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const handleViewHistory = async (doc) => {
    try {
      setSelectedDoc(doc);
      setShowHistoryModal(true);
      
      const fornecedorId = getEmpresaId();
      
      const listEnviados = httpsCallable(functions, 'listDocumentosEnviados');
      const result = await listEnviados({ 
        fornecedor_id: fornecedorId,
        documento_obrigatorio_id: doc.id
      });
      
      if (result.data.success) {
        setHistorico(result.data.data.documentos || []);
      }
    } catch (err) {
      console.error('❌ Erro ao carregar histórico:', err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pendente':
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: '📋 Pendente', icon: Clock };
      case 'em_analise':
        return { bg: 'bg-blue-100', text: 'text-blue-800', label: '🔄 Em Análise', icon: Clock };
      case 'aprovado':
        return { bg: 'bg-green-100', text: 'text-green-800', label: '✅ Aprovado', icon: CheckCircle };
      case 'rejeitado':
        return { bg: 'bg-red-100', text: 'text-red-800', label: '❌ Recusado', icon: XCircle };
      case 'vencido':
        return { bg: 'bg-red-100', text: 'text-red-800', label: '🔴 Vencido', icon: AlertTriangle };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: FileText };
    }
  };

  const getCardBorderColor = (doc) => {
    if (doc.alerta) {
      if (doc.alerta.urgencia === 'critica') return 'border-l-4 border-red-500 bg-red-50';
      if (doc.alerta.urgencia === 'alta') return 'border-l-4 border-orange-500 bg-orange-50';
      if (doc.alerta.urgencia === 'media') return 'border-l-4 border-yellow-500 bg-yellow-50';
    }
    if (doc.status_envio === 'aprovado') return 'border-l-4 border-green-500 bg-green-50';
    if (doc.status_envio === 'rejeitado') return 'border-l-4 border-red-500 bg-red-50';
    return 'border-l-4 border-gray-300 bg-white';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando documentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileText className="text-blue-600" />
                Documentos Obrigatórios
              </h1>
              <p className="text-gray-600 mt-2">
                Envie e acompanhe seus documentos obrigatórios
              </p>
            </div>
            <button
              onClick={loadDocumentos}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-600">Total</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-600">{stats.pendentes}</p>
                <p className="text-sm text-gray-600">Pendentes</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.em_analise}</p>
                <p className="text-sm text-blue-600">Em Análise</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{stats.aprovados}</p>
                <p className="text-sm text-green-600">Aprovados</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-600">{stats.alertas_criticos || 0}</p>
                <p className="text-sm text-red-600">Alertas</p>
              </div>
            </div>
          )}
        </div>

        {/* Lista de Documentos */}
        <div className="grid grid-cols-1 gap-4">
          {documentos.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum documento obrigatório</h3>
              <p className="text-gray-600">
                Cadastre ingredientes para visualizar os documentos obrigatórios aplicáveis.
              </p>
            </div>
          ) : (
            documentos.map(doc => {
              const badge = getStatusBadge(doc.status_envio);
              const StatusIcon = badge.icon;
              
              return (
                <div key={doc.id} className={`${getCardBorderColor(doc)} rounded-lg shadow-sm p-6`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{doc.nome}</h3>
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                          <StatusIcon className="h-3 w-3" />
                          {badge.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{doc.descricao}</p>
                      
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                        <span>📑 Tipo: {doc.tipo}</span>
                        <span>🔄 Frequência: {doc.frequencia}</span>
                        {doc.validade_dias && (
                          <span>⏱️ Validade: {doc.validade_dias} dias</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Alerta de Vencimento */}
                  {doc.alerta && (
                    <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${
                      doc.alerta.urgencia === 'critica' ? 'bg-red-100 border border-red-300' :
                      doc.alerta.urgencia === 'alta' ? 'bg-orange-100 border border-orange-300' :
                      'bg-yellow-100 border border-yellow-300'
                    }`}>
                      <AlertTriangle className={`h-5 w-5 ${
                        doc.alerta.urgencia === 'critica' ? 'text-red-700' :
                        doc.alerta.urgencia === 'alta' ? 'text-orange-700' :
                        'text-yellow-700'
                      }`} />
                      <span className={`text-sm font-medium ${
                        doc.alerta.urgencia === 'critica' ? 'text-red-700' :
                        doc.alerta.urgencia === 'alta' ? 'text-orange-700' :
                        'text-yellow-700'
                      }`}>
                        {doc.alerta.mensagem}
                      </span>
                    </div>
                  )}

                  {/* Último Envio */}
                  {doc.ultimo_envio && (
                    <div className="bg-gray-50 p-3 rounded-lg mb-4">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Último envio:</span> {doc.ultimo_envio.nome_arquivo}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Enviado em: {doc.ultimo_envio.data_upload?.toDate ? doc.ultimo_envio.data_upload.toDate().toLocaleString('pt-BR') : 'N/A'}
                      </p>
                      {doc.ultimo_envio.observacoes && (
                        <p className="text-sm text-red-600 mt-2">
                          <span className="font-medium">Motivo da recusa:</span> {doc.ultimo_envio.observacoes}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Instruções */}
                  {doc.instrucoes_upload && (
                    <div className="bg-blue-50 p-3 rounded-lg mb-4 border border-blue-200">
                      <p className="text-sm font-medium text-blue-900 mb-1">ℹ️ Instruções:</p>
                      <p className="text-sm text-blue-700">{doc.instrucoes_upload}</p>
                    </div>
                  )}

                  {/* Ações */}
                  <div className="flex gap-3">
                    {(doc.status_envio === 'pendente' || doc.status_envio === 'rejeitado' || doc.status_envio === 'vencido') && (
                      <button
                        onClick={() => handleOpenUploadModal(doc)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Upload className="h-4 w-4" />
                        {doc.status_envio === 'pendente' ? 'Enviar Documento' : 'Reenviar Documento'}
                      </button>
                    )}
                    
                    {doc.total_uploads > 0 && (
                      <button
                        onClick={() => handleViewHistory(doc)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <History className="h-4 w-4" />
                        Ver Histórico ({doc.total_uploads})
                      </button>
                    )}

                    {doc.ultimo_envio?.url_arquivo && (
                      <a
                        href={doc.ultimo_envio.url_arquivo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        Visualizar
                      </a>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal de Upload */}
      {showUploadModal && selectedDoc && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Enviar Documento</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedDoc.nome}</p>
              </div>
              <button onClick={handleCloseUploadModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-700">{success}</span>
              </div>
            )}

            {/* Informações do Documento */}
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Requisitos:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>📄 Formatos aceitos: {selectedDoc.formato_aceito.join(', ')}</li>
                <li>📦 Tamanho máximo: {selectedDoc.tamanho_maximo_mb}MB</li>
                {selectedDoc.validade_dias && (
                  <li>⏱️ Validade: {selectedDoc.validade_dias} dias após aprovação</li>
                )}
              </ul>
            </div>

            {/* Upload de Arquivo */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selecionar Arquivo *
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                accept={selectedDoc.formato_aceito.map(f => `.${f.toLowerCase()}`).join(',')}
              />
              {arquivo && (
                <p className="text-sm text-green-600 mt-2 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  {arquivo.name} ({(arquivo.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>

            {/* Instruções */}
            {selectedDoc.instrucoes_upload && (
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Instruções:</p>
                <p className="text-sm text-gray-600">{selectedDoc.instrucoes_upload}</p>
              </div>
            )}

            {/* Botões */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCloseUploadModal}
                disabled={uploading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpload}
                disabled={!arquivo || uploading}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Enviar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Histórico */}
      {showHistoryModal && selectedDoc && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Histórico de Envios</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedDoc.nome}</p>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-3">
              {historico.map((envio, index) => {
                const badge = getStatusBadge(envio.status);
                const StatusIcon = badge.icon;
                
                return (
                  <div key={envio.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-gray-900">Versão {envio.versao || (historico.length - index)}</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                            <StatusIcon className="h-3 w-3" />
                            {badge.label}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-1">
                          <span className="font-medium">Arquivo:</span> {envio.nome_arquivo}
                        </p>
                        
                        <p className="text-xs text-gray-500">
                          Enviado em: {envio.data_upload?.toDate ? envio.data_upload.toDate().toLocaleString('pt-BR') : 'N/A'}
                        </p>
                        
                        {envio.data_aprovacao && (
                          <p className="text-xs text-green-600 mt-1">
                            Aprovado em: {envio.data_aprovacao?.toDate ? envio.data_aprovacao.toDate().toLocaleString('pt-BR') : 'N/A'}
                          </p>
                        )}
                        
                        {envio.observacoes && (
                          <p className="text-sm text-red-600 mt-2">
                            <span className="font-medium">Motivo:</span> {envio.observacoes}
                          </p>
                        )}
                      </div>
                      
                      {envio.url_arquivo && (
                        <a
                          href={envio.url_arquivo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          <Eye className="h-3 w-3" />
                          Ver
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FornecedorDocumentosPage;


